/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import {
  Crown,
  Users as UsersIcon,
  Sparkles,
  UserPlus,
  ShieldQuestion,
} from "lucide-react";
import { Button, EmptyState } from "../ui";
import MemberCard from "./MemberCard";
import AdminBoardForm from "./AdminBoardForm";
import TeamsTab from "./TeamsTab";
import { boardApi } from "./api";
import "./BoardPage.css";

const TABS = [
  { id: "current", label: "Board" },
  { id: "elders", label: "Elders Council" },
  { id: "teams", label: "Volunteer Teams" },
  { id: "past", label: "Past" },
];

export default function BoardPage() {
  const [tab, setTab] = useState("current");
  const [current, setCurrent] = useState([]);
  const [elders, setElders] = useState([]);
  const [past, setPast] = useState([]);
  const [pastElders, setPastElders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("board"); // 'board' | 'elder'
  const [editing, setEditing] = useState(null);

  const isAdmin = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      // Respect the active-role view: an admin acting as a resident must not
      // see board edit controls. Legacy blobs (no active_role) stay admin.
      const actingAsAdmin = u.active_role ? u.active_role === "ADMIN" : true;
      return !!u.is_admin && actingAsAdmin;
    } catch (_) {
      return false;
    }
  })();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [a, c, p, pe] = await Promise.all([
        boardApi.listCurrent(),
        boardApi.listElders(),
        boardApi.listPast(),
        boardApi.listPastElders(),
      ]);
      setCurrent(a || []);
      setElders(c || []);
      setPast(p || []);
      setPastElders(pe || []);
    } catch (e) {
      setError(e.message || "Could not load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAddBoard = () => {
    setFormMode("board");
    setEditing(null);
    setFormOpen(true);
  };
  const openAddElder = () => {
    setFormMode("elder");
    setEditing(null);
    setFormOpen(true);
  };

  return (
    <div className="vsbd-shell">
      <div className="vsbd-head">
        <div>
          <h1>Board & Council</h1>
          <p className="vsbd-sub">
            The elected office-bearers and directors who run Vishal
            Sanjivini's day-to-day affairs, the Elders Council that
            settles disputes between the board and members, and the
            volunteer teams that support them. Board members serve a
            three-year term.
          </p>
        </div>
        {isAdmin && (
          <div className="vsbd-head-actions">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Sparkles size={14} />}
              onClick={openAddElder}
              disabled={elders.length >= 3}
              title={
                elders.length >= 3
                  ? "Elders Council is full (3 members). End a term first."
                  : "Add an elder"
              }
            >
              Add Elder
            </Button>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<UserPlus size={14} />}
              onClick={openAddBoard}
              disabled={current.length >= 8}
              title={
                current.length >= 8
                  ? "The board is full (8 members)."
                  : "Add a board member"
              }
            >
              Add Board Member
            </Button>
          </div>
        )}
      </div>

      <div className="vsbd-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`vsbd-tab ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.id === "current" && current.length > 0 && (
              <span className="vsbd-tab-count">{current.length}</span>
            )}
            {t.id === "elders" && elders.length > 0 && (
              <span className="vsbd-tab-count">{elders.length}</span>
            )}
            {t.id === "past" && (past.length + pastElders.length) > 0 && (
              <span className="vsbd-tab-count">{past.length + pastElders.length}</span>
            )}
          </button>
        ))}
      </div>

      {error && <div className="vsbd-error">{error}</div>}
      {loading && <div className="vsbd-empty">Loading…</div>}

      {!loading && tab === "current" && (
        <CurrentBoardView
          rows={current}
          isAdmin={isAdmin}
          onEdit={(m) => {
            setFormMode("board");
            setEditing(m);
            setFormOpen(true);
          }}
          onChanged={load}
        />
      )}

      {!loading && tab === "elders" && (
        <EldersView
          current={elders}
          isAdmin={isAdmin}
          onEdit={(e) => {
            setFormMode("elder");
            setEditing(e);
            setFormOpen(true);
          }}
          onChanged={load}
        />
      )}

      {!loading && tab === "teams" && <TeamsTab isAdmin={isAdmin} />}

      {!loading && tab === "past" && (
        <PastBoardView past={past} pastElders={pastElders} />
      )}

      <AdminBoardForm
        open={formOpen}
        mode={formMode}
        existing={editing}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          setEditing(null);
          load();
        }}
      />
    </div>
  );
}

function CurrentBoardView({ rows, isAdmin, onEdit, onChanged }) {
  if (rows.length === 0)
    return (
      <EmptyState
        icon={<Crown size={28} strokeWidth={1.4} />}
        title="No active board members"
        subtitle={
          isAdmin
            ? "Use the Add Board Member button above to seed the roster."
            : "An admin will add board members soon."
        }
      />
    );
  return (
    <div className="vsbd-grid">
      {rows.map((m) => (
        <MemberCard
          key={m.id}
          variant="board"
          member={m}
          dimmed={false}
          isAdmin={isAdmin}
          onEdit={() => onEdit(m)}
          onChanged={onChanged}
        />
      ))}
    </div>
  );
}

function PastBoardView({ past, pastElders }) {
  const all = [
    ...past.map((m) => ({ ...m, _variant: "board" })),
    ...pastElders.map((e) => ({ ...e, _variant: "elder" })),
  ].sort((a, b) => {
    const ad = a.end_date || a.start_date || "";
    const bd = b.end_date || b.start_date || "";
    return bd.localeCompare(ad);
  });
  if (all.length === 0)
    return (
      <EmptyState
        icon={<UsersIcon size={28} strokeWidth={1.4} />}
        title="No past members yet"
        subtitle="Board members and elders whose terms have ended will appear here."
      />
    );
  return (
    <div className="vsbd-grid">
      {all.map((m) => (
        <MemberCard
          key={m.id}
          variant={m._variant}
          member={m}
          dimmed={true}
          isAdmin={false}
        />
      ))}
    </div>
  );
}

function EldersView({ current, isAdmin, onEdit, onChanged }) {
  return (
    <>
      <div className="vsbd-elders-blurb">
        <ShieldQuestion size={18} strokeWidth={1.8} />
        <span>
          The Elders Council settles disputes between the Board and
          general members. Members are 55+, did not contest the
          previous Board election, and serve a 5-year term.
        </span>
      </div>
      {current.length === 0 ? (
        <EmptyState
          icon={<UsersIcon size={28} strokeWidth={1.4} />}
          title="Elders Council not yet seated"
          subtitle="The General Body will appoint three elders."
        />
      ) : (
        <div className="vsbd-grid">
          {current.map((e) => (
            <MemberCard
              key={e.id}
              variant="elder"
              member={e}
              dimmed={false}
              isAdmin={isAdmin}
              onEdit={() => onEdit(e)}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}
    </>
  );
}
